import type { ReactNode } from 'react';
import { cn } from './cn.js';
import { Icon } from './Icon.js';

/** A busca da barra. Quem filtra é quem chama — a barra só desenha o campo. */
export interface ToolbarSearch {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** O nome acessível do campo: "Pesquisar vistorias". */
  label: string;
}

/**
 * A barra acima da lista: busca à esquerda, ações à direita.
 *
 * É a barra da referência de 17/09 — campo de pesquisa, os botões da tela, o
 * gatilho de `Filters` e o de exportar. Ela mora aqui, e não em cada tela,
 * porque era exatamente o tipo de peça que nasce seis vezes com seis alturas —
 * o mesmo defeito que a medição de 01/09 achou nas larguras de conteúdo, onde
 * cada tela isolada estava certa e o conjunto não.
 *
 * ## Por que ela saiu de dentro da `Table` (17/09)
 *
 * Nasceu lá dentro, e por isso só existia onde havia tabela. O relatório de
 * demandas filtra o MESMO recorte da mesa e não tem tabela nenhuma — ele
 * mostra números —, então a barra dele teria que ser escrita à mão com um campo
 * de busca parecido. Duas barras parecidas na mesma jornada é como as seis
 * larguras começaram.
 *
 * A `Table` continua desenhando a sua quando recebe `search` ou `toolbar`:
 * quem tem tabela não precisa saber que esta peça existe.
 *
 * ## A borda é a `borda`, e não a `borda-forte`
 *
 * É o contrário do `Button`, e de propósito. Lá o contorno é a ÚNICA coisa que
 * diz "isto é um botão", e por isso ele não pode ser claro — está escrito no
 * `contorno`. Aqui o campo tem 44px de altura, uma lupa dentro e ocupa metade
 * da barra: o que o desenha é o tamanho, não a linha. Com a `borda-forte` ele
 * lia como um campo em erro ao lado dos botões, que é o peso que a referência
 * dá aos dois — o campo mais claro que o botão.
 *
 * O `hover` continua existindo e passou a ir de clara para forte, em vez de
 * forte para mais escura ainda.
 *
 * ## O campo é `<label>` com `<input>` dentro, e não um `Field`
 *
 * O `Field` da casa põe o rótulo ACIMA do campo, que é o certo num formulário e
 * errado numa barra: a barra é uma fileira de controles da mesma altura, e um
 * rótulo em cima do primeiro deles desalinharia todos os outros. Aqui o rótulo
 * vive no leitor de tela (`sr-only`) e a lupa faz o trabalho na tela —
 * concessão que só se paga porque é UM campo, com um uso só, e com o
 * `placeholder` dizendo o que se digita.
 */
export function Toolbar({
  search,
  toolbar,
  className,
}: {
  search?: ToolbarSearch;
  /** Os botões: "Nova vistoria", o `Filters`, "Exportar". */
  toolbar?: ReactNode;
  className?: string;
}) {
  if (!search && !toolbar) return null;

  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      {search && (
        <label
          className={cn(
            'flex h-11 min-w-50 flex-1 items-center gap-3 rounded-borda border border-borda',
            'bg-superficie px-4 transition-colors hover:border-borda-forte',
            'focus-within:border-primaria-texto',
          )}
        >
          <Icon nome="busca" size={16} className="text-texto-tenue" />
          <span className="sr-only">{search.label}</span>
          <input
            type="search"
            value={search.value}
            placeholder={search.placeholder ?? 'Digite para pesquisar'}
            onChange={(e) => search.onChange(e.target.value)}
            className="min-w-0 flex-1 bg-transparent text-sm text-texto outline-none placeholder:text-texto-tenue"
          />
        </label>
      )}
      {toolbar && (
        /*
         * **A altura é da BARRA, e não de cada botão que cai nela.**
         *
         * O campo de busca tem 44px e o `Button` da casa tem 36 — a medida da
         * barra de ações do topo, que é outro lugar. Lado a lado eles davam a
         * fileira desencontrada da referência ao contrário: o gatilho de
         * filtros mais baixo que o campo ao lado dele, com 4px de sobra em
         * cima e embaixo.
         *
         * `*:h-11` alcança o filho DIRETO, que é o `<button>` — o `Popover` do
         * `Filters` não desenha elemento nenhum em volta do gatilho. Assim a
         * tela não precisa lembrar de pedir altura nenhuma: quem entra na
         * barra já entra na medida dela.
         *
         * **E `*:border-borda` pela mesma razão, com outro efeito.** O
         * `contorno` do `Button` contorna com `primaria` desde 17/09 — era
         * `borda-forte` —, e o motivo está lá: solto numa página, o contorno é
         * a única coisa que diz que aquilo é um botão. Aqui ele não está solto
         * — está numa fileira, ao lado de um campo de 44px contornado com a
         * `borda` clara —, e a diferença entre os dois tons lia como se o
         * gatilho de filtros estivesse aceso ou em erro. Com o laranja a
         * diferença ficou maior, e a linha continua sendo a mesma: a referência
         * dá o mesmo peso de linha a tudo que está na barra.
         *
         * Só a COR: o botão primário não tem espessura de borda declarada,
         * então nele isto não desenha nada.
         */
        <div className="flex flex-wrap items-center gap-3 *:h-11 *:border-borda">
          {toolbar}
        </div>
      )}
    </div>
  );
}
